<template>
  <div
    class="details details--full client-details-view"
    v-resize="calculateTopSectionHeight"
  >
    <DetailsToolbar
      ref="header"
      :title="$route.meta.title"
      :blocked="isBlocked"
      :blockages="client.blockages"
      :table-name="tableName"
      :is-preview="isFullPanelOpened"
      :prev-route="prevRoute"
    >
      <v-spacer />
      <v-btn
        v-if="currentTab === 0"
        class="px-4 ml-6"
        :class="{ 'button-active': isMultiselectActive && multiselectSubject === 'generate'}"
        color="primary"
        outlined
        v-shortkey="multiselectShortkeys"
        @shortkey="toggleMultiselect('generate')"
        @click="toggleMultiselect('generate')"
      >
        {{ getLabelWithShortkey('Wygeneruj dokument zbiorczy', multiselectShortkeys.key) }}
      </v-btn>
      <v-btn
        v-else-if="currentTab === 1"
        class="px-4 ml-6"
        :class="{ 'button-active': isMultiselectActive && multiselectSubject === 'download'}"
        color="primary"
        outlined
        v-shortkey="multiselectShortkeys"
        @shortkey="toggleMultiselect('download')"
        @click="toggleMultiselect('download')"
      >
        {{ getLabelWithShortkey('Pobierz dokumenty', multiselectShortkeys.key) }}
      </v-btn>
      <v-btn
        v-if="tabs[currentTab].multiselect"
        class="px-4 ml-6"
        :class="{ 'button-active': isMultiselectActive && multiselectSubject === 'export'}"
        color="primary"
        outlined
        v-shortkey="exportShortkeys"
        @shortkey="toggleMultiselect('export')"
        @click="toggleMultiselect('export')"
      >
        {{ getLabelWithShortkey('Exportuj do csv', exportShortkeys.key) }}
      </v-btn>
      <ActionButtons
        v-if="getDetailsActions.length"
        :actions="getDetailsActions"
        :item="client"
      />
    </DetailsToolbar>
    <v-container
      fluid
      ref="topSection"
    >
      <v-row
        class="px-2"
        align="stretch"
      >
        <v-col
          v-for="(detail,index) in detailsSections"
          :key="detail.name"
          class="h-100"
        >
          <div class="detail-section__wrapper">
            <div
              class="detail-section__header d-flex justify-space-between align-center"
              v-if="detail.text"
            >
              {{ detail.text }}
              <v-icon
                v-if="detailsSections.length === index + 1"
                :ripple="false"
                @click="changeSectionsVisibility()"
              >
                {{ detailsSectionsVisibile ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </div>

            <div
              v-if="detailsSectionsVisibile"
              :class="{'detail-section__content': detail.text}"
            >
              <DetailsSection
                :detail="detail"
                :data="client"
              />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <Tabs
      :key="`tabs` + client.id"
      :tabs="tabs"
      class="pl-4 pt-2 mb-4 details-tabs"
      ref="tabs"
      persistent
      style="border-bottom: 1px solid #D7E0EB"
      @goTo="changeTab"
    />

    <v-tabs-items
      v-if="client.id"
      :key="client.id"
      v-model="currentTab"
      style="background-color: transparent"
    >
      <v-tab-item>
        <ClientTable
          table-name="clientOrders"
          :multiselect-actions="confirmationBarActions"
          :top-section-height="topSectionHeight"
        />
      </v-tab-item>
      <v-tab-item>
        <ClientTable
          table-name="clientInvoices"
          :multiselect-actions="confirmationBarActions"
          :top-section-height="topSectionHeight"
        />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientAddresses :client="client" />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientDocuments :client="client" />
      </v-tab-item>
      <v-tab-item>
        <ClientTable
          table-name="clientBDO"
          :top-section-height="topSectionHeight"
        />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientCallEntries :client-id="client.id" />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientContacts
          :client="client"
          :top-section-height="topSectionHeight"
        />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientBlockages :blockages="client.blockages" />
      </v-tab-item>
      <v-tab-item class="px-4">
        <ClientChanges :changes="client.clientChanges" />
      </v-tab-item>
      <v-tab-item
        class="px-4"
        v-if="client.isMiddleman"
      >
        <EndClients />
      </v-tab-item>
    </v-tabs-items>
    <CallEntryInput />
  </div>
</template>

<script>
import { getTableDetails, getBlockerValue } from '../../utils'
import actions from '../../const/actions'
import Tabs from '../Layout/Tabs'
import ActionButtons from '../Elements/ActionButtons'
import DetailsToolbar from './Sections/DetailsToolbar'
import DetailsSection from './Sections/DetailsSection'
import ClientAddresses from './Sections/ClientAddresses'
import ClientContacts from './Sections/ClientContacts'
import ClientDocuments from './Sections/ClientDocuments'
import ClientBlockages from './Sections/ClientBlockages'
import ClientChanges from './Sections/ClientChanges'
import ClientCallEntries from './Sections/ClientCallEntries'
import EndClients from './Sections/EndClients'
import ClientTable from './Sections/ClientTable'
import CallEntryInput from '../Elements/CallEntryInput'
import { mapState, mapActions } from 'vuex'
import { filterInactiveActions } from '../../utils/quickActions'
import { getLabelWithShortkey, multiselectShortkeys, exportShortkeys } from '../../const/shortKeys'
import webSocketMixin from '../../mixins/webSocketMixin.vue'
import navigateBackMixin from '../../mixins/navigateBackMixin'

export default {
  components: {
    Tabs,
    ActionButtons,
    DetailsToolbar,
    DetailsSection,
    ClientAddresses,
    ClientContacts,
    ClientDocuments,
    ClientBlockages,
    ClientChanges,
    ClientCallEntries,
    ClientTable,
    EndClients,
    CallEntryInput
  },
  provide () {
    return {
      notWebSocketHandled: false
    }
  },
  mixins: [
    webSocketMixin,
    navigateBackMixin // prevRoute
  ],
  data: () => ({
    tableName: 'clients',
    currentTab: 0,
    actions,
    multiselectShortkeys,
    exportShortkeys,
    multiselectSubject: 'export',
    topSectionHeight: 350,
    detailsSectionsVisibile: true
  }),
  channels: {
    ClientShowChannel: { received (data) { this.captureSingleChange(data) } }
  },
  computed: {
    ...mapState({
      client: state => state.client.entity,
      department: state => state.core.department,
      isMultiselectActive: function (state) {
        const tableName = this.currentTab === 0 ? 'orders' : 'invoices' // tabs order
        return state[tableName]?.isMultiselectActive
      },
    }),
    tabs() {
      const tabs = [
        { value: 0, text: 'Zlecenia', multiselect: true },
        { value: 1, text: 'Dokumenty', multiselect: true },
        { value: 2, text: 'Lokalizacje' },
        { value: 3, text: 'Załączniki' },
        { value: 4, text: 'BDO' },
        { value: 5, text: 'LOG CRM' },
        { value: 6, text: 'Osoby kontaktowe', actions: [] },
        { value: 7, text: 'Blokady', actions: [] },
        { value: 8, text: 'Historia klienta', actions: [] },
        { value: 9, text: 'Klienci końcowi', actions: [] },
      ]
      return this.client.isMiddleman ? tabs : tabs.slice(0, -1)
    },
    getDetailsActions () {
      const sharedActions = [
        { ...actions.manageBlockages },
        { ...actions.addNewPickup, props: { specificClient: true } },
        { ...actions.addNewSwap, props: { specificClient: true } },
        { ...actions.addNewOrder, create: true, props: { specificClient: true } },
      ]
      const tabActions = this.tabs[this.currentTab].actions || []
      const buttons = tabActions.concat(sharedActions)
      return filterInactiveActions(buttons, this.client, this.tableName)
    },
    detailsSections () {
      return getTableDetails(this.tableName).sections
    },
    isBlocked () {
      return getBlockerValue(this.client, this.tableName)
    },
    isFullPanelOpened () {
      return !!this.$route.query.full
    },
    confirmationBarActions () {
      const {
        generateInvoice, downloadMultipleInvoices, exportClientOrdersToCsv, exportClientInvoicesToCsv
      } = actions
      if (this.multiselectSubject === 'generate') {
        return [generateInvoice]
      } else if (this.multiselectSubject === 'download') {
        return [downloadMultipleInvoices]
      } else if (this.multiselectSubject === 'export') {
        if (this.currentTab === 0) {
          return [exportClientOrdersToCsv]
        } else if (this.currentTab === 1) {
          return [exportClientInvoicesToCsv]
        }
      }
      return []
    },
  },
  watch: {
    currentTab (newValue, oldValue) {
      if (this.tabs[oldValue].multiselect) {
        this.toggleMultiselectStatus(false)
      }
    },
    department (newDepartment) {
      if (newDepartment?.id) {
        this.fetchClientAddresses(this.$route.params.id)
      }
    }
  },
  mounted () {
    this.initClientView(this.$route.params.id)
  },
  beforeDestroy () {
    this.clearSingleClient()
    this.unsubscribeSocket('ClientShowChannel')
  },
  beforeRouteUpdate (to, from, next) {
    this.currentTab = 0
    this.initClientView(to.params.id)
    next()
  },
  methods: {
    ...mapActions({
      getSingleClient: 'client/getSingleClient',
      clearSingleClient: 'client/clearSingleClient',
      clearClientOrders: 'clientOrders/clearTableItems',
      clearClientBDO: 'clientBDO/clearTableItems',
      clearClientInvoices: 'clientInvoices/clearTableItems',
      setupCallEntry: 'callEntry/setupCallEntry',
      getAddresses: 'addresses/getItems',
      toggleMultiselectStatus: function (dispatch, show) {
        const tableName = this.currentTab === 0 ? 'orders' : 'invoices' // tabs order
        return dispatch(`${tableName}/toggleMultiselectStatus`, show)
      },
    }),
    initClientView(clientId) {
      const { query: { phoneNumber, callStart, contactId, callEntryId } } = this.$route
      if (callStart) this.setupCallEntry({ clientId: Number(clientId), phoneNumber, callStart, contactId: contactId ? Number(contactId) : null, callEntryId: Number(callEntryId) })
      this.fetchClientAddresses(clientId)
      this.getSingleClient(clientId)
        .finally(() => {
          this.$nextTick(() => {
            this.calculateTopSectionHeight()
          })
        })
      this.subscribeSocket('ClientShowChannel', { clientId })
    },
    fetchClientAddresses(clientId) {
      const departmentId = this.department?.id
      if (!clientId || !departmentId) return
      this.getAddresses({ params: { filters: { clientId, departmentIds: [departmentId] } } })
    },
    getLabelWithShortkey,
    toggleMultiselect (subject) {
      const prevSubject = this.multiselectSubject
      if (subject !== this.multiselectSubject) {
        this.multiselectSubject = subject
      }
      if (!this.isMultiselectActive || subject === prevSubject) {
        this.toggleMultiselectStatus(!this.isMultiselectActive)
      }
    },
    calculateTopSectionHeight () {
      const headerEl = this.$refs.header?.$el
      const tabsEl = this.$refs.tabs?.$el
      const topSectionEl = this.$refs.topSection
      if (!headerEl || !tabsEl || !topSectionEl) return
      this.topSectionHeight = (headerEl.clientHeight || 0) + (tabsEl.clientHeight || 0) + (topSectionEl.clientHeight || 0) + 18
      // 18 for tabs margin bottom
    },
    changeTab (tab) {
      this.currentTab = tab.value
    },
    changeSectionsVisibility() {
      this.detailsSectionsVisibile = !this.detailsSectionsVisibile
      this.$nextTick(() => {
        this.calculateTopSectionHeight()
      })
    }
  }
}
</script>
